import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OnboardingV2Service } from '../../services/onboarding-v2.service';
import { AppConfig } from '../../../shared/config/app-config';
import { ApiClientHelper } from '../../helpers/api-client.helper';
import { OtpResentMode } from '../../../kamona/model/kamonal-model';
import { LocalStorageService } from '../../services/local-storage.service';
import { LoginTypeRequest } from '../../models/forget-passcode';
import { UserNameType, PolicyDocumentType, PolicyResponse} from '../../../shared/models/utility.model';

@Injectable({
    providedIn: 'root',
})
export class ProfileSetupService {
    constructor(
        private router: Router,
        private apiClientHelper: ApiClientHelper,
        private config: AppConfig,
        private onboardingService: OnboardingV2Service,
        private localStorage: LocalStorageService,
    ) {}

    login() {
        this.onboardingService.login();
    }

    generateOtpForProfileSetup(payload: any, mode = OtpResentMode.SMS_MODE): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/self-onboarding/verifications/phone-numbers/start`;
        if (mode === OtpResentMode.WHATSAPP_MODE) {
            payload = { ...payload, channel: 'WHATSAPP' };
        }
        return this.apiClientHelper.postForPromise(url, payload);
    }

    handleErrorModal(params) {
        this.onboardingService.launchErrorModal(params);
    }

    saveUserTypePayload(payload) {
        this.localStorage.saveUserTypePayload(payload);
    }

    saveSignInResponse(res: any) {
        this.localStorage.save('sign-in-res', JSON.stringify(res));
    }

    getSignInResponse() {
        const res = this.localStorage.get('sign-in-res');
        if (res) {
            return JSON.parse(res);
        }
        return {
            ussdCode: '5573*733564#',
            identifier: '',
            channel: 'WEB',
            deviceUniqueIdentifier: '',
            loginType: LoginTypeRequest.PHONENUMBER,
            userNameType: UserNameType.PHONENUMBER,
        };
    }

    validateOtpForProfileSetup(payload): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/self-onboarding/verifications/phone-number/complete`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    termsAndConditions(): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/terms-and-conditions`;
        return this.apiClientHelper.getForPromise(url);
    }

    getPolicyDocuments(policyType: PolicyDocumentType): Promise<PolicyResponse> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/policy/${policyType}/download`;
        return this.apiClientHelper.getForPromise(url)
    }

    getUssdConfirmationStatus(payload): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/self-onboarding/verifications/phone-number/authorization-status`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    isOtpSuspension(error: any) {
        return this.onboardingService.isSuspended(error);
    }

    isProcessToolong(error: any) {
        return this.onboardingService.isProcessToolong(error);
    }

    profileFound(error: any) {
        return this.onboardingService.profileFound(error);
    }

    createProfile(payload): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v1/self-onboarding/steps/final/new`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    allowSignup() {
        return this.onboardingService.allowSignup();
    }

    createUserPin(payload): Promise<any> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v2/self-onboarding/set-user-pin`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    routeToKyc() {
        return this.onboardingService.routeToKycV2();
    }

    routeToLogin() {
        this.login();
    }

    createAccount() {
        this.onboardingService.routeToPage('/onboarding-v2/profile-setup');
    }

    routeToDeviceRegistration() {
        this.onboardingService.routeToPage('/user-device-registration');
    }

    forgotPasscode() {
        this.onboardingService.routeToPage('/login/forget-passcode');
    }

    forgotUsername() {
        this.onboardingService.routeToPage('/forgot-username');
    }

    forgotPassword() {
        this.onboardingService.routeToPage('/login/forget-password');
    }

    useProfileSetup() {
        return this.config.USE_PROFILE_SETUP;
    }

    useResendOtpViaWhatsapp() {
        return this.config.CAN_RESEND_OTP_VIA_WHATSAPP;
    }

    canUseNewUssd() {
        return this.config.CAN_USE_NEW_USSD;
    }

    logMixPanelEvent(params: any) {
        this.onboardingService.logMixpanelEvent(params);
    }

    useMoniepointMFBLogo() {
        return this.config.USE_MONIEPOINT_MFB_LOGO;
    }

    showLicensedText() {
        return this.config.SHOW_LICENSED_AND_SECURED_INFO;
    }

    showTermsAndConditionsCheckBox() {
        return this.config.SHOW_TERMS_AND_CONDITION_CHECKBOX;
    }

    getConfig() {
        return this.config;
    }
}
